import React, { Fragment, forwardRef } from 'react';
import Head from 'next/head';
import JsxParser from './react-jsx-parser/components/JsxParser';

const [resetFaqs, addFaq, getFaqs] = (() => {
  let qsAndAs = new Set();

  const reset = () => {
    qsAndAs = new Set();
  };

  const get = () => qsAndAs;

  const add = (text, isQ) => {
    const qOrA = JSON.stringify({ text, isQ });

    qsAndAs = new Set([...qsAndAs, qOrA]);
  };

  return [reset, add, get];
})();

// eslint-disable-next-line react/display-name
const Q = forwardRef(({ children }, { childrenAst }) => {
  if (childrenAst) {
    const q = new JsxParser({
      jsx: childrenAst,
      renderToHtml: true,
      allowUnknownElements: true,
    }).render2();
    addFaq(q, true);
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
});

// eslint-disable-next-line react/display-name
const A = forwardRef(({ children }, { childrenAst }) => {
  if (childrenAst) {
    const a = new JsxParser({
      jsx: childrenAst,
      renderToHtml: true,
      allowUnknownElements: true,
    }).render2();
    addFaq(a, false);
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
});

const pairQsAndAs = qsAndAs => {
  let last = false;

  return [...qsAndAs]
    .map(q => JSON.parse(q))
    .reduce(
      ([Qs, As], { text, isQ }) => {
        let out;

        if (!last && isQ) {
          out = [[...Qs, text], As];
        } else if (last && !isQ) {
          out = [Qs, [...As, text]];
        } else if (last && isQ) {
          out = [[...Qs.slice(0, -1), [...Qs.slice(-1), text].join('<br><br>')], As];
        } else if (!last && !isQ) {
          out = [Qs, [...As.slice(0, -1), [...As.slice(-1), text].join('<br><br>')]];
        }

        last = isQ;

        return out;
      },
      [[], []],
    );
};

const FaqSchema = () => {
  const [Qs, As] = pairQsAndAs(getFaqs());

  const minSize = Math.min(Qs.length, As.length);

  return minSize ? (
    <Head>
      <script
        type="application/ld+json"
        /* eslint-disable-next-line react/no-danger */
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [...Array(minSize).keys()].map(i => ({
              '@type': 'Question',
              name: Qs[i],
              acceptedAnswer: {
                '@type': 'Answer',
                text: As[i],
              },
            })),
          }),
        }}
      />
    </Head>
  ) : null;
};

export { Q, A, FaqSchema, resetFaqs };
